import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { StyledTooltip, Divider } from '@DesignSystem/data-display';
import useExperimentPanelsAvailability from '@shared/api/useExperimentPanelsAvailability';
import { ArrowTightIcon } from '@Icons-outdated';
import { animated, useSpring } from 'react-spring';
import difference from 'lodash/difference';

import {
  VIEWS_EXCLUDED_IN_COMPARE,
  ORDERED_DETAIL_VIEWS,
  DETAIL_VIEWS
} from '@experiment-management-shared/constants/experimentConstants';
import { VIEW_INFO } from '@experiment-details/constants/experiment-details';

import styles from './ExperimentDetailsSidebar.module.scss';

const COLLAPSED_MENU_WIDTH = '50px';
const EXPANDED_MENU_WIDTH = '184px';

const ExperimentDetailsSidebar = ({
  detailView,
  handleDetailViewChange,
  isMenuCollapsed,
  onExpandCollapseClick,
  isComparison,
  experimentKey,
  experiments
}) => {
  const VIEWS = Object.keys(VIEW_INFO);
  const COMPARE_VIEWS = difference(VIEWS, VIEWS_EXCLUDED_IN_COMPARE);

  const [isHovered, setIsHovered] = useState(false);
  const experimentKeys = experiments?.map(item => item.experimentKey);

  const { data: panelsAvailability } = useExperimentPanelsAvailability({
    experimentKeys: isComparison ? experimentKeys : [experimentKey]
  });

  const sortedDetailViews = useMemo(() => {
    const tabs = isComparison ? COMPARE_VIEWS : VIEWS;

    if (panelsAvailability) {
      const availabilityData = {
        ...panelsAvailability,
        [DETAIL_VIEWS.PANELS]: true // 'panels' always true - Design requirement
      };
      return tabs
        .sort((keyA, keyB) => {
          const valueA = availabilityData[ORDERED_DETAIL_VIEWS[keyA]] ?? false;
          const valueB = availabilityData[ORDERED_DETAIL_VIEWS[keyB]] ?? false;

          return valueA === valueB ? 0 : valueB ? 1 : -1;
        })
        .map(key => ({
          tabName: key,
          isDataAvailable: availabilityData[ORDERED_DETAIL_VIEWS[key]]
        }));
    }
    return tabs;
  }, [panelsAvailability, isComparison, COMPARE_VIEWS, VIEWS]);

  const handleExpandCollapseClick = () => {
    onExpandCollapseClick(!isMenuCollapsed);
  };

  const renderDetailItemLabel = tabName => {
    const { displayName } = VIEW_INFO[tabName];
    const { icon: Icon } = VIEW_INFO[tabName];

    return (
      <>
        <div className={styles.itemIconContainer}>
          <Icon className={styles.itemIcon} />
        </div>
        {!isMenuCollapsed && displayName}
      </>
    );
  };

  const collapseAnimationProps = useSpring({
    width: isMenuCollapsed ? COLLAPSED_MENU_WIDTH : EXPANDED_MENU_WIDTH
  });

  const arrowAnimationProps = useSpring({
    opacity: isHovered || isMenuCollapsed ? 1 : 0
  });

  const renderTabItem = (item, index, array) => {
    const { tabName, isDataAvailable } =
      typeof item === 'string'
        ? { tabName: item, isDataAvailable: false }
        : item;
    const id = `${tabName}-tab`;
    const { displayName } = VIEW_INFO[tabName];
    const showDivider =
      isDataAvailable === false &&
      index !== 0 &&
      array[index - 1].isDataAvailable === true;

    return (
      <StyledTooltip
        key={tabName}
        title={displayName}
        placement="right"
        disableHoverListener={!isMenuCollapsed}
      >
        <span>
          {showDivider && (
            <span className={styles.noDataDivider}>
              <Divider margin="2px" />
            </span>
          )}
          <li
            onClick={() => handleDetailViewChange(tabName)}
            key={tabName}
            id={id}
            className={cx(styles.detailItem, {
              [styles.selectedDetailItem]: detailView === tabName,
              [styles.noDataDetailItem]: isDataAvailable === false
            })}
            role="button"
            data-test={displayName}
          >
            {renderDetailItemLabel(tabName)}
          </li>
        </span>
      </StyledTooltip>
    );
  };

  return (
    <animated.div
      className={cx(styles.sideBarMenu, {
        [styles.collapsed]: isMenuCollapsed
      })}
      style={collapseAnimationProps}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <animated.div
        className={styles.collapseExpandButton}
        style={arrowAnimationProps}
        onClick={handleExpandCollapseClick}
      >
        <ArrowTightIcon />
      </animated.div>

      <ul>{sortedDetailViews.map(renderTabItem)}</ul>
    </animated.div>
  );
};

ExperimentDetailsSidebar.propTypes = {
  detailView: PropTypes.string.isRequired,
  handleDetailViewChange: PropTypes.func.isRequired,
  isMenuCollapsed: PropTypes.bool.isRequired,
  onExpandCollapseClick: PropTypes.func.isRequired,
  isComparison: PropTypes.bool.isRequired,
  experimentKey: PropTypes.string.isRequired,
  experiments: PropTypes.array.isRequired
};

export default ExperimentDetailsSidebar;
